"use client";

/**
 * Dictionary client — fetcher dla publicznych słowników z backend
 * (`IIAPI_CONTROLLER::IAPI_getPublicDictionary` via `/api/iiapi/dictionary/{type}`).
 *
 * **Use case:** komponenty visitor (CContactForm, CUserAccounts profile,
 * RegisterForm) potrzebują populowanych dropdown'ów (countries, provinces, sex).
 *
 * **Cache:** in-memory per session. Cache key = `{type}|{country}|{locale}`.
 * - Countries / sex / months się NIE zmieniają — cache nieskończony.
 * - Provinces zależą od `country` — osobny cache entry per kraj.
 * - Lokale: jeśli user przełączy `[locale]` segment, cache miss -> re-fetch
 *   (countries w innym języku).
 *
 * **Bez SWR / React Query** — wystarczy lightweight cache w-memory; słowniki
 * małe (~250 krajów × ~10 chars = ~2.5KB), pobierane raz, używane wielokrotnie.
 *
 * @example
 * ```tsx
 * "use client";
 * import { useDictionary } from "@/lib/api/dictionary";
 *
 * function CountrySelect() {
 *   const { items, loading, error } = useDictionary("countries");
 *   if (loading) return <div>Ładowanie…</div>;
 *   if (error) return <div>{error}</div>;
 *   return (
 *     <select>
 *       <option value="">—</option>
 *       {items.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
 *     </select>
 *   );
 * }
 * ```
 */

import { useEffect, useState } from "react";
import { useParams } from "next/navigation";

export type DictionaryItem = {
  id: string;
  name: string;
};

export type DictionaryType =
  | "countries"
  | "languages"
  | "currency"
  | "provinces"
  | "sex"
  | "days"
  | "months"
  | "employment_formats"
  | "fiance_ways";

/* In-memory cache — żyje w obrębie current page session.
   Wartość = Promise<DictionaryItem[]> (in-flight deduplication: równoległe
   wywołania dla tego samego klucza dostają TĘ SAMĄ promise, jeden fetch). */
const cache = new Map<string, Promise<DictionaryItem[]>>();

function cacheKey(type: DictionaryType, country: string | undefined, locale: string): string {
  return `${type}|${country ?? ""}|${locale}`;
}

/**
 * Niski-level fetcher — bez React hook'a. Używaj gdy potrzebujesz słownika
 * spoza komponentu (np. validate w schemie, server action).
 *
 * **Cache:** wynik cache'owany per `{type, country, locale}`. Drugie wywołanie
 * z tymi samymi argumentami -> instant resolve z cache (no fetch).
 *
 * @throws Error gdy fetch failed (network / 5xx / invalid type)
 */
export async function fetchDictionary(
  type: DictionaryType,
  options?: { country?: string; locale?: string },
): Promise<DictionaryItem[]> {
  const country = options?.country;
  const locale = options?.locale ?? "pl";
  const key = cacheKey(type, country, locale);

  const cached = cache.get(key);
  if (cached) return cached;

  const queryParts: string[] = [];
  if (country) queryParts.push(`country=${encodeURIComponent(country)}`);
  if (locale) queryParts.push(`lang=${encodeURIComponent(locale)}`);
  const qs = queryParts.length > 0 ? `?${queryParts.join("&")}` : "";

  const promise = (async () => {
    const res = await fetch(`/api/iiapi/dictionary/${encodeURIComponent(type)}${qs}`, {
      method: "GET",
      headers: { Accept: "application/json" },
    });
    const body = (await res.json().catch(() => null)) as {
      ok?: boolean;
      items?: Record<string, DictionaryItem[]>;
      message?: string;
    } | null;

    if (!res.ok || !body?.ok) {
      /* On error — usuwamy cache entry żeby retry potem był możliwy. */
      cache.delete(key);
      throw new Error(body?.message ?? `Nie udało się pobrać słownika "${type}".`);
    }

    return body.items?.[type] ?? [];
  })();

  cache.set(key, promise);
  return promise;
}

/**
 * React hook — loaduje słownik z auto-refresh przy zmianie locale / country.
 *
 * **Locale:** wykrywa automatycznie z URL `[locale]` segment (useParams).
 * Override przez `options.locale` (zwykle nie potrzeba).
 *
 * **Country (dla provinces):** wymagany dla `type="provinces"`. Empty value
 * -> skip fetch (`items: []`, `loading: false`, no error). Gdy `country`
 * zmieni się — refetch.
 *
 * **Loading state:** `loading: true` PRZED pierwszym successful fetch'em
 * (lub PO zmianie deps gdy cache miss). Cache hit -> `loading: false` od razu.
 */
export function useDictionary(
  type: DictionaryType,
  options?: { country?: string },
): {
  items: DictionaryItem[];
  loading: boolean;
  error: string | null;
} {
  const params = useParams();
  const rawLocale = Array.isArray(params?.locale) ? params.locale[0] : (params?.locale as string | undefined);
  const locale = (rawLocale ?? "pl").toLowerCase().slice(0, 2);
  const country = options?.country;

  const [items, setItems] = useState<DictionaryItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    /* Provinces wymagają country — bez niego nie strzelamy fetch'a, zwracamy
       pustą listę (caller pokaże disabled select / hint "wybierz najpierw kraj"). */
    if (type === "provinces" && !country) {
      setItems([]);
      setLoading(false);
      setError(null);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);

    fetchDictionary(type, { country, locale })
      .then((result) => {
        if (cancelled) return;
        setItems(result);
        setLoading(false);
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        setError(e instanceof Error ? e.message : String(e));
        setItems([]);
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [type, country, locale]);

  return { items, loading, error };
}
